import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import whatsappFlowsAPI from '../../api/whatsappFlows';

const state = {
  records: [],
  selectedFlow: null,
  steps: {},
  enrollments: {},
  reports: {},
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingSteps: false,
    isFetchingEnrollments: false,
    isFetchingReports: false,
  },
};

export const getters = {
  getFlows: _state => _state.records,
  getActiveFlows: _state => _state.records,
  getFlow: _state => id => _state.records.find(flow => flow.id === id),
  getSelectedFlow: _state => _state.selectedFlow,
  getSteps: _state => flowId => _state.steps[flowId] || [],
  getEnrollments: _state => flowId => _state.enrollments[flowId] || [],
  getReport: _state => (flowId, reportType) =>
    _state.reports[`${flowId}_${reportType}`],
  getUIFlags: _state => _state.uiFlags,
};

export const actions = {
  // Flows
  async get({ commit }) {
    commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, { isFetching: true });
    try {
      const response = await whatsappFlowsAPI.getFlows();
      commit(
        types.default.SET_WHATSAPP_FLOWS,
        response.data.payload || response.data
      );
    } finally {
      commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, { isFetching: false });
    }
  },

  async show({ commit }, flowId) {
    commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await whatsappFlowsAPI.getFlow(flowId);
      commit(types.default.SET_SELECTED_WHATSAPP_FLOW, response.data);
      return response.data;
    } finally {
      commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, {
        isFetchingItem: false,
      });
    }
  },

  async create({ commit }, flowData) {
    commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, { isCreating: true });
    try {
      const response = await whatsappFlowsAPI.createFlow(flowData);
      commit(types.default.ADD_WHATSAPP_FLOW, response.data);
      return response.data;
    } finally {
      commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, { isCreating: false });
    }
  },

  async update({ commit }, { flowId, flowData }) {
    commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, { isUpdating: true });
    try {
      const response = await whatsappFlowsAPI.updateFlow(flowId, flowData);
      commit(types.default.EDIT_WHATSAPP_FLOW, response.data);
      return response.data;
    } finally {
      commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, { isUpdating: false });
    }
  },

  async delete({ commit }, flowId) {
    commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, { isDeleting: true });
    try {
      await whatsappFlowsAPI.deleteFlow(flowId);
      commit(types.default.DELETE_WHATSAPP_FLOW, flowId);
    } finally {
      commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, { isDeleting: false });
    }
  },

  // Flow Actions
  async activateFlow({ commit }, flowId) {
    const response = await whatsappFlowsAPI.activateFlow(flowId);
    commit(types.default.EDIT_WHATSAPP_FLOW, response.data);
    return response.data;
  },

  async pauseFlow({ commit }, flowId) {
    const response = await whatsappFlowsAPI.pauseFlow(flowId);
    commit(types.default.EDIT_WHATSAPP_FLOW, response.data);
    return response.data;
  },

  async archiveFlow({ commit }, flowId) {
    const response = await whatsappFlowsAPI.archiveFlow(flowId);
    commit(types.default.EDIT_WHATSAPP_FLOW, response.data);
    return response.data;
  },

  async unarchiveFlow({ commit }, flowId) {
    const response = await whatsappFlowsAPI.unarchiveFlow(flowId);
    commit(types.default.EDIT_WHATSAPP_FLOW, response.data);
    return response.data;
  },

  // Steps
  async getSteps({ commit }, flowId) {
    commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, { isFetchingSteps: true });
    try {
      const response = await whatsappFlowsAPI.getSteps(flowId);
      commit(types.default.SET_WHATSAPP_FLOW_STEPS, {
        flowId,
        steps: response.data,
      });
      return response.data;
    } finally {
      commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, {
        isFetchingSteps: false,
      });
    }
  },

  async createStep({ commit }, { flowId, stepData }) {
    const response = await whatsappFlowsAPI.createStep(flowId, stepData);
    commit(types.default.ADD_WHATSAPP_FLOW_STEP, {
      flowId,
      step: response.data,
    });
    return response.data;
  },

  async updateStep({ commit }, { flowId, stepId, stepData }) {
    const response = await whatsappFlowsAPI.updateStep(
      flowId,
      stepId,
      stepData
    );
    commit(types.default.EDIT_WHATSAPP_FLOW_STEP, {
      flowId,
      step: response.data,
    });
    return response.data;
  },

  async deleteStep({ commit }, { flowId, stepId }) {
    await whatsappFlowsAPI.deleteStep(flowId, stepId);
    commit(types.default.DELETE_WHATSAPP_FLOW_STEP, { flowId, stepId });
  },

  // Enrollments
  async getEnrollments({ commit }, { flowId, params }) {
    commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, {
      isFetchingEnrollments: true,
    });
    try {
      const response = await whatsappFlowsAPI.getEnrollments(flowId, params);
      const enrollments = response.data.payload || response.data;
      const meta = response.data.meta || null;
      commit(types.default.SET_WHATSAPP_FLOW_ENROLLMENTS, {
        flowId,
        enrollments,
      });
      return { enrollments, meta };
    } finally {
      commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, {
        isFetchingEnrollments: false,
      });
    }
  },

  async createEnrollment({ commit }, { flowId, contactId, enrollmentData }) {
    const response = await whatsappFlowsAPI.createEnrollment(
      flowId,
      contactId,
      enrollmentData
    );
    const enrollment = response.data?.enrollment || response.data;
    commit(types.default.ADD_WHATSAPP_FLOW_ENROLLMENT, {
      flowId,
      enrollment,
    });
    return enrollment;
  },

  async pauseEnrollment({ commit }, { flowId, enrollmentId }) {
    const response = await whatsappFlowsAPI.pauseEnrollment(
      flowId,
      enrollmentId
    );
    commit(types.default.EDIT_WHATSAPP_FLOW_ENROLLMENT, {
      flowId,
      enrollment: response.data,
    });
    return response.data;
  },

  async resumeEnrollment({ commit }, { flowId, enrollmentId }) {
    const response = await whatsappFlowsAPI.resumeEnrollment(
      flowId,
      enrollmentId
    );
    commit(types.default.EDIT_WHATSAPP_FLOW_ENROLLMENT, {
      flowId,
      enrollment: response.data,
    });
    return response.data;
  },

  async cancelEnrollment({ commit }, { flowId, enrollmentId }) {
    await whatsappFlowsAPI.cancelEnrollment(flowId, enrollmentId);
    commit(types.default.DELETE_WHATSAPP_FLOW_ENROLLMENT, {
      flowId,
      enrollmentId,
    });
  },

  async enrollByLabel(context, { flowId, label, enrollmentData = {} }) {
    const response = await whatsappFlowsAPI.enrollByLabel(
      flowId,
      label,
      enrollmentData
    );
    return response.data;
  },

  async enrollByLabelStatus(context, { flowId, jobId }) {
    const response = await whatsappFlowsAPI.enrollByLabelStatus(flowId, jobId);
    return response.data;
  },

  receiveEnrollmentUpdate({ commit }, enrollment) {
    if (!enrollment || !enrollment.whatsapp_flow_id) {
      return;
    }

    commit(types.default.EDIT_WHATSAPP_FLOW_ENROLLMENT, {
      flowId: enrollment.whatsapp_flow_id,
      enrollment,
    });
  },

  // Reports
  async getSummary({ commit }, flowId) {
    commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, {
      isFetchingReports: true,
    });
    try {
      const response = await whatsappFlowsAPI.getSummary(flowId);
      commit(types.default.SET_WHATSAPP_FLOW_REPORT, {
        flowId,
        reportType: 'summary',
        data: response.data,
      });
      return response.data;
    } finally {
      commit(types.default.SET_WHATSAPP_FLOW_UI_FLAG, {
        isFetchingReports: false,
      });
    }
  },

  async getEnrollmentTimeline(context, { flowId, params }) {
    const response = await whatsappFlowsAPI.getEnrollmentTimeline(
      flowId,
      params
    );
    return response.data;
  },

  async getStatusDistribution(context, flowId) {
    const response = await whatsappFlowsAPI.getStatusDistribution(flowId);
    return response.data;
  },

  async getCancelledContacts(context, { flowId, params = {} }) {
    const response = await whatsappFlowsAPI.getCancelledContacts(
      flowId,
      params
    );
    return response.data;
  },

  async getMessageAnalytics(context, flowId) {
    const response = await whatsappFlowsAPI.getMessageAnalytics(flowId);
    return response.data;
  },

  async exportEnrollments(context, { flowId, params }) {
    const response = await whatsappFlowsAPI.exportEnrollments(flowId, params);
    return response.data;
  },
};

export const mutations = {
  [types.default.SET_WHATSAPP_FLOW_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },

  [types.default.SET_WHATSAPP_FLOWS]: MutationHelpers.set,

  [types.default.ADD_WHATSAPP_FLOW]: MutationHelpers.create,

  [types.default.EDIT_WHATSAPP_FLOW]: MutationHelpers.update,

  [types.default.DELETE_WHATSAPP_FLOW]: MutationHelpers.destroy,

  [types.default.SET_SELECTED_WHATSAPP_FLOW](_state, flow) {
    _state.selectedFlow = flow;
  },

  [types.default.SET_WHATSAPP_FLOW_STEPS](_state, { flowId, steps }) {
    _state.steps = { ..._state.steps, [flowId]: steps };
  },

  [types.default.ADD_WHATSAPP_FLOW_STEP](_state, { flowId, step }) {
    const currentSteps = _state.steps[flowId] || [];
    _state.steps = { ..._state.steps, [flowId]: [...currentSteps, step] };
  },

  [types.default.EDIT_WHATSAPP_FLOW_STEP](_state, { flowId, step }) {
    const steps = _state.steps[flowId] || [];
    const index = steps.findIndex(s => s.id === step.id);
    if (index !== -1) {
      steps[index] = step;
      _state.steps = { ..._state.steps, [flowId]: [...steps] };
    }
  },

  [types.default.DELETE_WHATSAPP_FLOW_STEP](_state, { flowId, stepId }) {
    const steps = _state.steps[flowId] || [];
    _state.steps = {
      ..._state.steps,
      [flowId]: steps.filter(s => s.id !== stepId),
    };
  },

  [types.default.SET_WHATSAPP_FLOW_ENROLLMENTS](
    _state,
    { flowId, enrollments }
  ) {
    _state.enrollments = { ..._state.enrollments, [flowId]: enrollments };
  },

  [types.default.ADD_WHATSAPP_FLOW_ENROLLMENT](_state, { flowId, enrollment }) {
    const current = _state.enrollments[flowId] || [];
    _state.enrollments = {
      ..._state.enrollments,
      [flowId]: [...current, enrollment],
    };
  },

  [types.default.EDIT_WHATSAPP_FLOW_ENROLLMENT](
    _state,
    { flowId, enrollment }
  ) {
    const enrollments = _state.enrollments[flowId] || [];
    const index = enrollments.findIndex(e => e.id === enrollment.id);
    if (index !== -1) {
      enrollments[index] = enrollment;
      _state.enrollments = {
        ..._state.enrollments,
        [flowId]: [...enrollments],
      };
    }
  },

  [types.default.DELETE_WHATSAPP_FLOW_ENROLLMENT](
    _state,
    { flowId, enrollmentId }
  ) {
    const enrollments = _state.enrollments[flowId] || [];
    _state.enrollments = {
      ..._state.enrollments,
      [flowId]: enrollments.filter(e => e.id !== enrollmentId),
    };
  },

  [types.default.SET_WHATSAPP_FLOW_REPORT](
    _state,
    { flowId, reportType, data }
  ) {
    _state.reports = {
      ..._state.reports,
      [`${flowId}_${reportType}`]: data,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
